import {course_getCourseById_Api, course_getCourses_Api, courseSchema} from 'apis'
import {getId} from 'apis/model/base'
import classNames from 'classnames'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {Button} from 'antd'
import {PiWarningCircleFill} from 'react-icons/pi'
import {BsArchive, BsInboxesFill} from 'react-icons/bs'
import {IoClose} from 'react-icons/io5'
import {HiMiniAdjustmentsHorizontal} from 'react-icons/hi2'
import {FaChalkboard, FaUserGraduate} from 'react-icons/fa6'
import {ClassActionTypes, CourseActionTypes} from 'views/AppLocals/Course'
import {SelectWidget} from 'views/AppLocals/custom/ReactSelectComponents'
import {useCourseForm} from 'views/AppLocals/Course/CourseClass/useCourseForm'
import {useCourseClassForm} from 'views/AppLocals/Course/CourseClass/useCourseClassForm'
import {useSelectEntities} from 'redux/entities/useSelectEntities'
import {formatNumber} from 'helpers/number'
import {filterValue} from 'helpers/filterOption'
import {useRouteSearchParam} from 'envs/_current/history'
import {useRouterSearchParam} from 'modules/navigation/useOnLocationChange'
import {CourseContainerContext} from './CourseContainer'
import CourseClassDetail from './CourseClassDetail'
import CourseSchedulePanel from './CourseSchedulePanel'
import CourseDetail from './CourseDetail'
import {CourseHeroHeader, getWeekProgress} from './CourseHeroHeader'
import {StatCard} from './CourseDashboard'
import {buildCourseRelLink} from '../functions/courseWorkspaceLinks'
import {ClassroomSvg} from 'components/Svgs/ClassroomSvg'
import WorkspaceContext from '../../WorkspaceContext'
import Pure from "../../../../components/Pure";

// Trang "Điều hành Khóa & Lớp học" - vận hành hằng ngày, KHÔNG tự vẽ header/tab/nội dung khóa hay
// lớp song song nữa (bạn yêu cầu "dùng lại, không clone") - chỉ còn 2 việc:
//   1. Băng thống kê + banner + card "Bộ Quản Lý Tiến Trình Khóa Học" (2 dropdown chọn khóa/lớp +
//      2 nút tạo nhanh) - phần RIÊNG của trang này, không có ở đâu khác.
//   2. Khi đã chọn khóa: nhúng THẲNG <CourseDetail/> (đầy đủ header + TabNavigation 6 tab + nội dung
//      từng tab y hệt trang "Danh sách Khóa học") - chỉ khác 2 prop hideBackLink (ẩn nút "Quay lại
//      danh sách Khóa học", đứng đây khóa đã hiển thị nối tiếp qua selector riêng) và overviewExtra
//      (nhúng thêm CourseSchedulePanel vào cuối tab Tổng quan - thứ CourseDetail.js gốc không có, vì
//      không phải màn vận hành hằng ngày). Khi đã chọn thêm LỚP: nhúng THẲNG <CourseClassDetail/>
//      (đầy đủ header + tab của lớp, tự có nút "Quay lại khóa" quay về đúng state current_course_class_id
//      rỗng) - CÙNG state current_course_id/current_course_class_id qua CourseContainerContext, không
//      cần đồng bộ tay. Cả 2 component đọc context y hệt cách CourseContainer.js (trang "Danh sách
//      Khóa học") đang dùng - tự fetch dữ liệu riêng, không nhận owner_id/owner_type qua props.

const EmptyBlockPlaceholder = ({children}) => (
    <div
        className="py-5 flex flex-col items-center gap-2 background-100 border border-dashed border-color-100 rounded-lg">
        <BsInboxesFill size={28} style={{color: '#dedede'}}/>
        <span className="text-center text-xs text-color-300 italic tracking-wide px-4">
            {children}
        </span>
    </div>
)

// Nhãn option cho 2 dropdown chọn khóa/lớp - mã khóa/mã lớp dạng tag mono đứng đầu (bạn yêu cầu),
// tên theo sau, suffix phụ (vd sĩ số lớp) nếu có. Dùng CHUNG cho cả 2 dropdown.
//
// KHÔNG dùng flex lồng bên trong (bản trước dùng flex items-center + flex-shrink-0 + min-w-0) - antd
// Select tự định vị/đo phần "giá trị đã chọn" + input tìm kiếm chồng lên nhau dựa trên bề rộng nội
// dung, 1 flex-box con có border/padding riêng bên trong dễ vỡ layout, tràn ra ngoài khung input
// (bạn báo lỗi này). Đổi hẳn sang inline-block/align-middle - vẫn 1 dòng chữ bình thường dưới góc
// nhìn của trình duyệt, an toàn hơn nhiều khi bị đo/định vị bởi thư viện ngoài.
const IdcodeOptionLabel = ({idcode, title, suffix}) => (
    <span className="truncate">
        {!!idcode && (
            <span style={{height: 30}} className="inline-flex flex-center mr-1.5 px-1.5 py-0.5 font-mono font-bold text-2xs rounded background-100 text-color-300">
                {idcode}
            </span>
        )}
        <span className="align-middle">{title}</span>
        {!!suffix && <span className="align-middle ml-1 text-color-300">{suffix}</span>}
    </span>
)

const CourseClassOperationsContent = ({owner_id, owner_type}) => {
    const t = useTranslate()
    const {organization} = useContext(WorkspaceContext)
    const {
        current_course_id,
        current_course_class_id,
        onSelectCourseContainerRoute,
    } = useContext(CourseContainerContext)

    const [inProcessCourses, setInProcessCourses] = useState([])
    const [enrollingCourses, setEnrollingCourses] = useState([])
    const [closedCourseCount, setClosedCourseCount] = useState(0)
    const [selectedCourse, setSelectedCourse] = useState({})
    const [showOngoingBanner, setShowOngoingBanner] = useState(true)

    // status_text ở fetchItems() (BE) chỉ nhận 1 giá trị/lần (không có CSV OR như delivery_mode) -
    // gọi 2 lần riêng (in_process + enrollment_open) rồi gộp, thay vì đổi BE cho 1 chỗ dùng này. Bạn
    // yêu cầu thêm khóa "đang tuyển sinh" (enrollment_open) vào dropdown, trước đây chỉ lấy
    // in_process ("đang diễn ra") - CHỈ đổi phạm vi dropdown Bước 1 này, không đụng filter khác.
    const {handleAsyncAction: getInProcessCourses, isLoading: isLoadingInProcessCourses} = useAsyncAction({
        apiInfo: course_getCourses_Api,
        onSuccess: (result, data) => {
            // course_getCourses_Api dùng createListTransform (mặc định str='data.data') - response.data
            // vẫn là {data: [...], meta: {...}} nguyên bản (chưa "phẳng" ra mảng), khác
            // course_getCourseById_Api (item đơn, response.data LÀ object luôn) - lấy đúng
            // response.data.data mới ra mảng khóa học.
            setInProcessCourses(_.get(data, 'response.data.data', []))
        },
    })
    const {handleAsyncAction: getEnrollingCourses, isLoading: isLoadingEnrollingCourses} = useAsyncAction({
        apiInfo: course_getCourses_Api,
        onSuccess: (result, data) => {
            setEnrollingCourses(_.get(data, 'response.data.data', []))
        },
    })
    // Băng thống kê đầu trang - thẻ "Khóa Học Đã Đóng/Lưu Trữ" cần đúng số khóa status_text=completed
    // (khớp định nghĩa CourseStates 'completed' đang dùng xuyên suốt file này) - chỉ cần TỔNG SỐ, gọi
    // per_page:1 rồi đọc meta.pagination.total (chuẩn IlluminatePaginatorAdapter, không tải hết data).
    const {handleAsyncAction: getClosedCoursesCount} = useAsyncAction({
        apiInfo: course_getCourses_Api,
        onSuccess: (result, data) => {
            setClosedCourseCount(_.get(data, 'response.data.meta.pagination.total', 0))
        },
    })
    const isLoadingOngoingCourses = isLoadingInProcessCourses || isLoadingEnrollingCourses
    const ongoingCourses = useMemo(() => (
        _.uniqBy([...inProcessCourses, ...enrollingCourses], 'id')
    ), [inProcessCourses, enrollingCourses])
    // Băng thống kê "lớp"/"học viên đang hoạt động" - dùng LẠI đúng con số organization.workspace_settings
    // .courses.statistics (active_course_class_count/active_student_count) mà CourseDashboard.js (tab
    // Tổng quan) đang dùng cho 2 thẻ tương ứng, KHÔNG tự cộng dồn lại từ ongoingCourses ở trang này -
    // tránh 2 cách tính khác nhau ra 2 con số lệch nhau cho cùng khái niệm "đang hoạt động".
    const orgCourseStats = organization?.workspace_settings?.courses?.statistics ?? {}
    const {handleAsyncAction: getCourseById, isLoading: isLoadingCourse} = useAsyncAction({
        apiInfo: course_getCourseById_Api,
        onSuccess: (result, data) => {
            // useAsyncAction bắn onSuccess qua setTimeout và có thể lặp lại khi entry asyncId
            // (dùng chung với getCourseById của CourseDetail nhúng bên trong) bị merge lại
            // {isLoading:true} mà vẫn giữ success:true. Bỏ qua payload rỗng + chỉ set khi khóa
            // thực sự đổi -> idempotent, không đẩy currentCourse nhấp nháy.
            const dataItem = _.get(data, 'response.data')
            const nextId = getId(dataItem)
            if (!nextId) return
            setSelectedCourse((prev) => (getId(prev) === nextId ? prev : dataItem))
        },
    })

    const {handleAction: handleCourseAction, modal: courseModal, refreshKey: courseRefreshKey} = useCourseForm({
        owner_id,
        owner_type
    })
    const {
        handleAction: handleClassAction,
        modal: classModal,
        refreshKey: classRefreshKey,
    } = useCourseClassForm({course: selectedCourse})

    // "Menu này mục đích chỉ lấy các khóa đang diễn ra HOẶC đang tuyển sinh" - tải cả
    // status_text=in_process VÀ enrollment_open (khớp CourseStates trong WorkspaceCourse.js), vẫn
    // loại "đã kết thúc" (completed).
    useEffect(() => {
        if (owner_id && owner_type) {
            getInProcessCourses({status_text: 'in_process', per_page: 200}, {type: owner_type, id: owner_id})
            getEnrollingCourses({status_text: 'enrollment_open', per_page: 200}, {type: owner_type, id: owner_id})
            getClosedCoursesCount({status_text: 'completed', per_page: 1}, {type: owner_type, id: owner_id})
        }
    }, [owner_id, owner_type, courseRefreshKey])

    useEffect(() => {
        if (current_course_id) {
            getCourseById({}, {id: current_course_id})
        } else {
            setSelectedCourse({})
        }
        // courseRefreshKey - sau khi sửa khóa qua modal (useCourseForm/COURSE_UPDATE) phải load lại
        // đúng khóa đang xem, không chỉ refresh mỗi dropdown 1.
    }, [current_course_id, classRefreshKey, courseRefreshKey])

    const currentCourse = useSelectEntities(getId(selectedCourse), courseSchema, selectedCourse)
    // Header Khóa hiện lại phía TRÊN <CourseClassDetail/> khi đã chọn lớp (bạn yêu cầu - trước đây
    // chọn lớp là mất hẳn ngữ cảnh khóa) cần weekProgress riêng cho thanh tiến trình của chính nó.
    const weekProgress = useMemo(
        () => getWeekProgress(currentCourse?.time_open, currentCourse?.time_close),
        [currentCourse?.time_open, currentCourse?.time_close]
    )

    // Mã khóa/mã lớp dạng tag đứng đầu mỗi option (bạn yêu cầu) - label giữ NGUYÊN dạng ReactNode (Select
    // ở đây là antd Select, nhận label ReactNode bình thường cho cả dòng trong list lẫn giá trị đã
    // chọn) - filterOption mặc định của SelectWidget (helpers/filterOption.js) lại cần option.label
    // là CHUỖI để tìm kiếm, nên KHÔNG dùng label để search nữa mà thêm field searchText riêng (xem
    // filterOptionBySearchText + prop filterOption truyền tay bên dưới, ghi đè default của SelectWidget).
    const courseOptions = useMemo(
        () => ongoingCourses.map((c) => ({
            value: c.id,
            label: <IdcodeOptionLabel idcode={c.course_idcode} title={c.title}/>,
            searchText: c.course_idcode ? `${c.course_idcode} ${c.title}` : c.title,
        })),
        [ongoingCourses]
    )
    // "Nếu danh sách này có thì chọn cái đầu tiên để hiển thị" - tự chọn khóa ĐẦU TIÊN khi danh sách
    // vừa tải xong mà chưa có khóa nào đang chọn, đỡ phải bấm chọn tay mỗi lần vào màn này. CHỈ tự
    // chọn 1 LẦN (hasAutoSelectedCourseRef) - không lặp lại mỗi khi current_course_id rỗng, tránh
    // "cướp" lại lựa chọn ngay sau khi admin bấm nút xoá (x) trên dropdown để cố tình bỏ chọn.
    const hasAutoSelectedCourseRef = useRef(false)
    useEffect(() => {
        if (!hasAutoSelectedCourseRef.current && !current_course_id && ongoingCourses.length > 0) {
            hasAutoSelectedCourseRef.current = true
            onSelectCourseContainerRoute({current_course_id: ongoingCourses[0].id, current_course_class_id: ''})
        }
    }, [ongoingCourses, current_course_id])
    // "Lớp trong Khóa đang chạy" - ưu tiên lọc course_classes theo active_as_current (BE: to_date
    // null hoặc chưa qua now). Fallback về TẤT CẢ lớp của khóa nếu lọc xong rỗng (vd dữ liệu cũ/test
    // chưa khai to_date đúng, hoặc khóa chỉ toàn lớp đã kết thúc) - tránh dropdown "No data" chết cứng.
    const courseClassesRaw = currentCourse?.course_classes ?? []
    const ongoingClasses = useMemo(() => {
        const active = courseClassesRaw.filter((cls) => cls.active_as_current)
        return active.length > 0 ? active : courseClassesRaw
    }, [courseClassesRaw])
    const classOptions = useMemo(
        () => ongoingClasses.map((cls) => ({
            value: cls.id,
            label: (
                <IdcodeOptionLabel
                    idcode={cls.course_class_idcode}
                    title={cls.title}
                    suffix={cls.student_count ? `(${cls.student_count} ${t('student').toLowerCase()})` : null}
                />
            ),
            searchText: cls.course_class_idcode ? `${cls.course_class_idcode} ${cls.title}` : cls.title,
        })),
        [ongoingClasses, t]
    )
    // filterOption mặc định của SelectWidget cần option.label là chuỗi (xem comment ở courseOptions) -
    // dùng chung 1 hàm filter riêng cho cả 2 dropdown mã hoá/lớp, so khớp trên searchText thay vì label.
    const filterOptionBySearchText = (value, option) => filterValue(value, option.searchText || '')

    // Dropdown 1 rỗng có 2 lý do khác hẳn nhau: (a) đang tải, (b) tải xong nhưng KHÔNG có khóa nào
    // status_text=in_process - phải nói rõ lý do (b) ngay trên card chọn khóa, tránh admin tưởng lỗi
    // tải dữ liệu trong khi bản chất là chưa có khóa nào đang giảng dạy.
    const hasNoOngoingCourse = !isLoadingOngoingCourses && courseOptions.length === 0

    return (
        <div className="w-full min-h-screen">
            {/* Băng thống kê tổng quan đầu trang - 4 chỉ số THẬT: khóa đang mở (đang chiêu sinh/vận
                hành, cùng nguồn ongoingCourses đang dùng cho dropdown 1), lớp/học viên cộng dồn từ
                đúng các khóa đang mở đó (course_class_count/student_count có sẵn trên từng khóa,
                không tự bịa), khóa đã đóng/lưu trữ (status_text=completed, đếm qua meta.pagination.total)
                - dòng phụ "Tổng cộng" của thẻ học viên lấy từ CÙNG nguồn stats tổng org đã dùng ở
                CourseDashboard.js (organization.workspace_settings.courses.statistics), không tính lại. */}
            <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4">
                <StatCard
                    title={t('courses currently open')}
                    value={formatNumber(ongoingCourses.length)}
                    subText={t('active')}
                    color="orange"
                    icon={<FaChalkboard size={18}/>}
                    href={buildCourseRelLink({rel: 'course', 'course-state': ''})}
                />
                <StatCard
                    title={t('classes in ongoing courses')}
                    value={formatNumber(orgCourseStats.active_course_class_count)}
                    subText={t('classes in session')}
                    color="indigo"
                    icon={<ClassroomSvg size={18}/>}
                    href={buildCourseRelLink({rel: 'course-class'})}
                />
                <StatCard
                    title={t('active students total')}
                    value={formatNumber(orgCourseStats.active_student_count)}
                    subText={orgCourseStats.student_count > 0 ? `${formatNumber(orgCourseStats.student_count)} ${t('total')}` : undefined}
                    color="green"
                    icon={<FaUserGraduate size={18}/>}
                    href={buildCourseRelLink({rel: 'student'})}
                />
                <StatCard
                    title={t('closed archived courses')}
                    value={formatNumber(closedCourseCount)}
                    subText={t('view archive')}
                    color="blue"
                    icon={<BsArchive size={18}/>}
                    href={buildCourseRelLink({rel: 'course', 'course-state': 'completed'})}
                />
            </div>

            {hasNoOngoingCourse && (
                <div className="mb-4 px-4 py-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl">
                    <PiWarningCircleFill size={16} className="mt-0.5 text-amber-600 flex-shrink-0"/>
                    <span className="text-sm text-amber-700 tracking-wide">
                        {t('no ongoing courses warning')}
                    </span>
                </div>
            )}
            {/* Băng thông báo tổng số khóa đang vận hành - chỉ hiện khi CÓ khóa (ngược lại đã có
                cảnh báo màu hổ phách phía trên) - số liệu thật (ongoingCourses.length), có thể đóng. */}
            {!hasNoOngoingCourse && showOngoingBanner && (
                <div className="mb-4 px-4 py-3 flex items-start gap-2 bg-green-50 border border-green-200 rounded-xl">
                    <PiWarningCircleFill size={16} className="mt-0.5 text-green-600 flex-shrink-0"/>
                    <span className="flex-1 text-sm text-green-700 tracking-wide">
                        {`${t('system managing ongoing courses')} ${ongoingCourses.length} ${t('main ongoing courses suffix')}`}
                    </span>
                    <IoClose
                        size={16}
                        onClick={() => setShowOngoingBanner(false)}
                        className="mt-0.5 flex-shrink-0 text-green-500 hover:text-green-700 cursor-pointer"
                    />
                </div>
            )}

            {/* Bộ chọn khóa/lớp + nút tạo nhanh */}
            <div className="mb-6 flex flex-col gap-4 p-4 background shadow-lg rounded-xl">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-start gap-2 min-w-0">
                        <HiMiniAdjustmentsHorizontal size={20} className="mt-1 flex-shrink-0 text-primary"/>
                        <div className="flex flex-col min-w-0">
                            <h3 className="font-bold text-base text-color-000 tracking-wide">
                                {t('progress management panel')}
                            </h3>
                            <span className="text-xs text-color-300 tracking-wide">
                                {t('progress management panel description')}
                            </span>
                        </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                        <Button
                            onClick={() => handleCourseAction(CourseActionTypes.COURSE_CREATE)}
                            className="px-4 font-semibold rounded-lg shadow-md"
                            style={{background: '#f59e0b', borderColor: '#f59e0b', color: '#fff'}}>
                            {`+ ${t('create course')}`}
                        </Button>
                        <Button
                            disabled={!currentCourse?.id}
                            onClick={() => handleClassAction(ClassActionTypes.CLASS_CREATE)}
                            className="px-4 font-semibold rounded-lg shadow-md"
                            style={!currentCourse?.id ? {} : {
                                background: '#1c4a75',
                                borderColor: '#1c4a75',
                                color: '#fff'
                            }}>
                            {`+ ${t('create class')}`}
                        </Button>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between gap-2">
                            <label className="font-bold text-xs text-color-300 uppercase tracking-wider">
                                {`1. ${t('select course to manage')}`}
                            </label>
                            <span
                                className="px-2 py-0.5 flex-shrink-0 rounded-full background-100 font-medium text-2xs text-color-300 whitespace-no-wrap">
                                {t('filtering ongoing courses only')}
                            </span>
                        </div>
                        <SelectWidget
                            size="large"
                            className="w-full course-picker-select"
                            allowClear
                            showSearch
                            filterOption={filterOptionBySearchText}
                            loading={isLoadingOngoingCourses}
                            options={courseOptions}
                            value={current_course_id || undefined}
                            placeholder={t('choose an ongoing course')}
                            onChange={(v) => onSelectCourseContainerRoute({
                                current_course_id: v || '',
                                current_course_class_id: ''
                            })}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between gap-2">
                            <label
                                className={classNames('font-bold text-xs uppercase tracking-wider', current_course_id ? 'text-color-300' : 'text-color-400')}>
                                {`2. ${t('select class in course')}`}
                            </label>
                            {classOptions.length > 0 && (
                                <span
                                    className="px-2 py-0.5 flex-shrink-0 rounded-full background-100 font-medium text-2xs text-color-300 whitespace-no-wrap">
                                    {`${t('classes count prefix')} ${classOptions.length} ${t('classes attached')}`}
                                </span>
                            )}
                        </div>
                        <SelectWidget
                            size="large"
                            className="w-full course-picker-select"
                            allowClear
                            showSearch
                            filterOption={filterOptionBySearchText}
                            disabled={!current_course_id}
                            loading={isLoadingCourse}
                            options={classOptions}
                            value={current_course_class_id || undefined}
                            placeholder={current_course_id ? t('choose a class') : t('choose course first')}
                            onChange={(v) => onSelectCourseContainerRoute({current_course_class_id: v || ''})}
                        />
                    </div>
                </div>
            </div>

            {!current_course_id && (
                <EmptyBlockPlaceholder>
                    {t('select an ongoing course above to manage its info')}
                </EmptyBlockPlaceholder>
            )}

            {/* Đã chọn khóa (và có thể cả lớp) - nhúng THẲNG CourseDetail/CourseClassDetail, KHÔNG tự
                vẽ header/tab/nội dung song song nữa (bạn yêu cầu "dùng lại, không clone") - đúng
                CÙNG logic switch mà CourseContainer.js (trang "Danh sách Khóa học") đang dùng, chỉ
                khác: có thêm selector khóa/lớp riêng của trang này ở trên, và CourseDetail.js nhận
                thêm overviewExtra (CourseSchedulePanel) + hideBackLink. */}
            {/* Gate CHỈ theo current_course_id (URL param, ổn định) - KHÔNG kèm currentCourse?.id.
                currentCourse lấy qua useSelectEntities bị các API list (per_page:200) normalize đè
                entity 'courses' -> .id chớp tắt -> <CourseDetail> unmount/remount liên tục ->
                nested getCourseById bắn lại, merge {isLoading:true} vào entry asyncId dùng chung
                (success vẫn true) -> useAsyncAction ở đây lặp onSuccess -> render loop + ResizeObserver
                của Select nổ. CourseDetail/CourseClassDetail tự fetch + tự guard khóa chưa có nên
                không cần chặn ở đây (CourseContainer.js bản standalone cũng chỉ gate current_course_id). */}
            {!!current_course_id && (
                !!current_course_class_id ? (
                    // Đã chọn thêm LỚP - CourseClassDetail tự có header/back-to-course RIÊNG của nó,
                    // nhưng chỉ nói về LỚP - bạn báo mất hẳn ngữ cảnh KHÓA (trước đây chọn lớp là che
                    // khuất luôn header khóa). Hiện lại CourseHeroHeader của khóa NGAY TRÊN, gap-6
                    // (không mb-6 riêng, tránh cộng dồn khoảng cách) để "nhìn được xuyên suốt" khóa ->
                    // lớp cùng lúc, khớp đúng yêu cầu + chú thích trong ảnh bạn gửi.
                    <div className="flex flex-col gap-6">
                        {!!currentCourse?.id && (
                            <CourseHeroHeader
                                currentCourse={currentCourse}
                                className="rounded-2xl shadow-lg"
                                stats={[
                                    {value: courseClassesRaw.length, label: t('class count')},
                                    {value: currentCourse?.student_count ?? 0, label: t('students')},
                                    {
                                        value: weekProgress ? `${weekProgress.totalWeeks} ${t('week')}` : '—',
                                        label: t('duration')
                                    },
                                ]}
                            />
                        )}
                        <CourseClassDetail/>
                    </div>
                ) : (
                    <Pure input={[current_course_id, owner_id, owner_type]}>
                        <CourseDetail
                            hideBackLink
                            overviewExtra={
                                // key={current_course_id} - buộc REMOUNT hẳn khi đổi khóa thay vì chỉ cập
                                // nhật prop course_id (bạn báo lịch không refresh theo khi đổi khóa ở
                                // select phía trên - dù deps của useAsync trong CourseSchedulePanel.js đã
                                // có course_id, remount là cách CHẮC CHẮN nhất: state nội bộ (collapsed,
                                // action/asyncId) reset sạch, useAsync tự fetch lại ngay khi mount thay vì
                                // trông chờ đúng nhánh so sánh dep cũ/mới của thư viện async-cache).
                                <CourseSchedulePanel
                                    key={current_course_id}
                                    owner_id={owner_id}
                                    owner_type={owner_type}
                                    course_id={current_course_id}
                                    eyebrow="course schedule management"
                                    title="course schedule statistics"
                                    collapsible
                                    compactUpcomingOnly
                                />
                            }
                        />

                    </Pure>
                )
            )}

            {courseModal}
            {classModal}
            <div className="h-16"/>
        </div>
    )
}

const CourseClassOperations = ({owner_id, owner_type}) => {
    const currentCourseIdParam = useRouteSearchParam('current_course_id')
    const currentCourseClassIdParam = useRouteSearchParam('current_course_class_id')
    const [current_course_id, setCurrentCourseId] = useState(currentCourseIdParam)
    const [current_course_class_id, setCurrentCourseClassId] = useState(currentCourseClassIdParam)
    const {onChangeSearchParams} = useRouterSearchParam({})

    const onSelectCourseContainerRoute = (values) => {
        if (_.has(values, 'current_course_id')) setCurrentCourseId(values?.current_course_id)
        if (_.has(values, 'current_course_class_id')) setCurrentCourseClassId(values?.current_course_class_id)
        onChangeSearchParams(values, 'replace')
    }

    return (
        <CourseContainerContext.Provider
            value={{
                current_course_id, setCurrentCourseId,
                current_course_class_id, setCurrentCourseClassId,
                onSelectCourseContainerRoute,
            }}>
            <CourseClassOperationsContent owner_id={owner_id} owner_type={owner_type}/>
        </CourseContainerContext.Provider>
    )
}

export default CourseClassOperations
